import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Spin, Empty, message, Popconfirm, Space } from 'antd';
import { PlusOutlined, ReloadOutlined, ExperimentOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Head } from './Head';
import { CreateTestLectureModal } from './CreateTestLectureModal';
import { formatDate } from '../utils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://translation-manag-server.dev.torsunov.pro';

export function TestLecturesPage({ onLogout }) {
    const navigate = useNavigate();
    const [testLectures, setTestLectures] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchTestLectures = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/projects/test_lectures`);
            if (!response.ok) {
                throw new Error('Failed to fetch test lectures');
            }
            const data = await response.json();
            setTestLectures(data.items || []);
        } catch (error) {
            console.error('Error fetching test lectures:', error);
            message.error('Не удалось загрузить список тестовых лекций');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTestLectures();
    }, [fetchTestLectures]);

    const handleCreateSuccess = () => {
        fetchTestLectures();
    };

    const handleRowClick = (record) => {
        navigate(`/project/${record.id}`);
    };

    const handleDelete = async (e, record) => {
        e.stopPropagation(); // Предотвращаем переход на страницу проекта
        try {
            const response = await fetch(`${API_BASE_URL}/projects/test_lecture/${record.id}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to delete test lecture');
            }
            message.success(`Лекция "${record.name}" удалена`);
            fetchTestLectures();
        } catch (error) {
            console.error('Error deleting test lecture:', error);
            message.error(`Ошибка при удалении: ${error.message}`);
        }
    };

    const columns = [
        {
            title: '#',
            dataIndex: 'order_number',
            key: 'order_number',
            width: 60,
            render: (value) => (
                <Tag color="blue">{value || '-'}</Tag>
            ),
        },
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            render: (text) => (
                <span style={{ fontWeight: 500 }}>{text}</span>
            ),
        },
        {
            title: 'Исходный файл',
            dataIndex: 'source_file',
            key: 'source_file',
            render: (text) => text || <span style={{ color: '#999' }}>—</span>,
        },
        {
            title: 'Дата создания',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (text) => text ? formatDate(text, true) : <span style={{ color: '#999' }}>—</span>,
        },
        {
            title: 'Тип',
            key: 'type',
            width: 120,
            render: () => (
                <Tag icon={<ExperimentOutlined />} color="orange">
                    Тестовая
                </Tag>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Popconfirm
                        title="Удалить лекцию?"
                        description={`Вы уверены, что хотите удалить "${record.name}"? Это действие нельзя отменить.`}
                        onConfirm={(e) => handleDelete(e, record)}
                        onCancel={(e) => e.stopPropagation()}
                        okText="Удалить"
                        cancelText="Отмена"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <CreateTestLectureModal
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onSuccess={handleCreateSuccess}
                testLecturesCount={testLectures.length}
            />

            <Head activeTab="test-lectures" onLogout={onLogout}>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchTestLectures}
                    loading={isLoading}
                >
                    Обновить
                </Button>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsModalOpen(true)}
                    style={{
                        backgroundColor: '#fa8c16',
                        borderColor: '#fa8c16',
                    }}
                >
                    Создать тестовую лекцию
                </Button>
            </Head>

            <div
                style={{
                    padding: '24px',
                    background: '#f5f5f5',
                    minHeight: 'calc(100vh - 64px)',
                }}
            >
                <div
                    style={{
                        background: '#fff',
                        borderRadius: '8px',
                        padding: '24px',
                    }}
                >
                    <div style={{ marginBottom: '16px' }}>
                        <h2 style={{ margin: 0 }}>
                            <ExperimentOutlined style={{ marginRight: '8px', color: '#fa8c16' }} />
                            Тестовые лекции
                        </h2>
                        <p style={{ color: '#666', marginTop: '8px' }}>
                            Здесь отображаются лекции, предназначенные для тестирования функциональности системы.
                        </p>
                    </div>

                    <Spin spinning={isLoading} tip="Загрузка...">
                        {testLectures.length === 0 && !isLoading ? (
                            <Empty
                                description="Тестовые лекции не найдены"
                                style={{ padding: '60px 0' }}
                            >
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsModalOpen(true)}
                                    style={{
                                        backgroundColor: '#fa8c16',
                                        borderColor: '#fa8c16',
                                    }}
                                >
                                    Создать первую тестовую лекцию
                                </Button>
                            </Empty>
                        ) : (
                            <Table
                                dataSource={testLectures}
                                columns={columns}
                                rowKey="id"
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: false,
                                    showTotal: (total) => `Всего: ${total} лекций`,
                                }}
                                onRow={(record) => ({
                                    onClick: () => handleRowClick(record),
                                    style: { cursor: 'pointer' },
                                })}
                                rowClassName={() => 'clickable-row'}
                            />
                        )}
                    </Spin>
                </div>
            </div>

            <style>{`
        .clickable-row:hover {
          background-color: #fafafa;
        }
      `}</style>
        </>
    );
}
